import moment from 'moment'
import {storageInUse} from './localStorage'
import {IFormField} from 'models/Common'
import {IUserCategory} from 'models/CMS'

export function getLocalStorageKey<T, K>(
 key: string,
 defaultValue?: K
) {
 const value = storageInUse.getItem(key)
 return value ? (JSON.parse(value) as T) : defaultValue
}

export function setLocalStorageKey<T>(
 key: string,
 value: T
) {
 storageInUse.setItem(key, JSON.stringify(value))
}

export function removeLocalStorageKey(key: string) {
 storageInUse.removeItem(key)
}

export function handleMultiPromiseResult<T>(
 result: PromiseSettledResult<T>,
 defaultValue: T
) {
 return result.status === 'fulfilled'
  ? result.value
  : defaultValue
}

export function getCookieValue(key: string) {
 if (typeof window !== 'undefined') {
  return document.cookie
   .split('; ')
   .find((row) => row.startsWith(`${key}=`))
   ?.split('=')[1]
 }
}

export function fixNextPageUrl(url?: string | null) {
 const regex = /^http:\/\//
 if (url != null && regex.test(url)) {
  return url.replace(regex, 'https://')
 }
 return url
}

export function convertSlugToString(slug: string) {
 return slug.split('-').join(' ')
}

export function getTimeStamp(date: string) {
 const dayInS = 86400
 const hrInS = 3600
 const postDateTime = new Date(date)
 const currentDateTime = new Date()
 const diff =
  (currentDateTime.getTime() - postDateTime.getTime()) /
  1000
 const days = Math.floor(diff / dayInS)
 const hours = Math.floor((diff % dayInS) / hrInS)
 const minutes = Math.round(((diff % dayInS) % hrInS) / 60)
 if (days === 0 && hours === 0 && minutes === 0)
  return 'Just Now'
 if (days === 0 && hours === 0) return `${minutes}m`
 if (days === 0) return `${hours}h`
 let diffDt = `${postDateTime.getDate()} ${postDateTime.toLocaleString(
  'default',
  {
   month: 'long',
  }
 )}`
 if (
  currentDateTime.getFullYear() !==
  postDateTime.getFullYear()
 ) {
  diffDt += ` ${postDateTime.getFullYear()}`
 }
 return diffDt
}

export function isArray<T>(array?: T[] | null) {
 return Array.isArray(array) && array.length > 0
}

export function createUUID() {
 let dt = new Date().getTime()
 const uuid =
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
   /[xy]/g,
   function (c) {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
   }
  )
 return uuid
}

export const htmlToString = (html: string) =>
 html
  .replace(/<[^>]+>/g, '')
  .trim()
  .replace(/\n/g, '')

export function getThumbnailUrl(url: string) {
 const splitArray = url.split('/')
 return `https://img.youtube.com/vi/${
  splitArray[splitArray.length - 1]
 }/0.jpg`
}

export function slugToString(slug: string) {
 return slug.split('-').join(' ')
}

const padNumberWithZero = (num: number) => {
 return num.toString().padStart(2, '0')
}

export function secondsToMinSec(inSeconds: number) {
 const minutes = Math.floor(inSeconds / 60)
 const seconds = inSeconds - minutes * 60
 return `${padNumberWithZero(minutes)}:${padNumberWithZero(
  seconds
 )}`
}

export const persistToken = (
 key: string,
 value = '',
 expiresIn = -1
) => {
 document.cookie = `${key}=${value}; max-age=${expiresIn}; path=/; samesite=strict;`
}

export function numDifferentiation(value: number) {
 const val = Math.abs(value)
 if (val >= 10000000)
  return `${(value / 10000000).toFixed(2)} Cr`
 if (val >= 100000)
  return `${(value / 100000).toFixed(2)} Lac`
 return value
}

export function getFileExtension(fileName: string) {
 if (typeof fileName !== 'string') return ''
 return fileName ? fileName?.split('.').pop() : ''
}

export function getFileName(fileName: string) {
 return fileName
  ? fileName
     .replace(/[^a-zA-Z0-9. ]/g, '')
     .replace(/ /g, '-')
  : ''
}

export function getFileType(fileType: string) {
 let docType = ''

 if (fileType.match('image.*')) {
  docType = 'image'
 } else if (fileType.match('video.*')) {
  docType = 'video'
 } else if (fileType.match('audio.*')) {
  docType = 'audio'
 } else if (
  fileType.includes('pdf') ||
  fileType.includes('document')
 ) {
  docType = fileType = 'document'
 } else {
  docType = 'other'
 }

 return docType
}

export function getFileTypeFromFileName(fileName: string) {
 let docType = ''
 const extension =
  getFileExtension(fileName)?.toLocaleLowerCase()
 if (!extension) {
  return ''
 }
 if (
  ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(
   extension
  )
 ) {
  docType = 'image'
 } else if (extension === 'pdf') {
  docType = 'document'
 } else if (
  ['mp4', 'mov', 'avi', 'wmv', 'mkv', 'flv'].includes(
   extension
  )
 ) {
  docType = 'video'
 } else {
  docType = 'other'
 }

 return docType
}

export const toAbsoluteImageUrl = (url: string) => {
 const imageBasePath = process.env.PUBLIC_URL
 if (!url) return imageBasePath
 if (
  url?.indexOf('http://') === 0 ||
  url?.indexOf('https://') === 0
 ) {
  return url
 } else {
  return imageBasePath + url
 }
}

export const isValidRegex = (s: string) => {
 try {
  return s ? !!new RegExp(s) : false
 } catch (e) {
  return false
 }
}

export const generateRandomString = (length: number) => {
 let result = ''
 const characters =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
 const charactersLength = characters.length
 for (let i = 0; i < length; i++) {
  result += characters.charAt(
   Math.floor(Math.random() * charactersLength)
  )
 }
 return result
}

export const generateRandomNo = (length: number) => {
 const randomIndex = Math.floor(Math.random() * length)

 return randomIndex
}

export const formatDateForDB = (value: Date | null) => {
 try {
  if (!value) return value
  const m = moment(value).format('YYYY-MM-DD')
  return m
 } catch (e) {
  return ''
 }
}

export const formatDateForDisplay = (
 value: string | undefined | null,
 format?: string | undefined
) => {
 try {
  if (!value) return value
  const m = moment(value).format(format ?? 'DD-MMM-YYYY')
  return m
 } catch (e) {
  return false
 }
}

export const formatNumberForDisplay = (
 value?: number | null
) => {
 try {
  if (!value) return '-'
  const num = Number(value)
  if (isNaN(num)) return '-'
  const m = num.toLocaleString('en-IN')
  return m
 } catch (e) {
  return ''
 }
}

export const formatString = (value?: string | null) => {
 if (!value) return ''
 return (
  value.charAt(0).toUpperCase() +
  value.slice(1).toLowerCase()
 )
}

export const convertEpochToDate = (
 value: string | number | undefined
) => {
 try {
  if (!value) return ''
  const date = new Date(Number(value) * 1000)

  const year = date.getFullYear() // 2023
  const month = date.getMonth() // 2 (months are 0-indexed, so March is 2)
  const day = date.getDate() // 15
  return `${year}-${(month + 1)
   .toString()
   .padStart(2, '0')}-${day.toString().padStart(2, '0')}`
 } catch (e) {
  return ''
 }
}

export const convertToDateEpoch = (
 value: string | undefined
) => {
 try {
  if (!value) return ''
  const someDate = new Date(value)
  const epochMilliseconds = someDate.getTime()
  return Math.floor(epochMilliseconds / 1000)
 } catch (e) {
  return ''
 }
}

export const getAccessValue = (
 data: IFormField,
 payload: IFormField
) => {
 let res = false
 if (data) {
  const userCategories = data?.user_categories?.map(
   (val: IUserCategory) => val.DehaatUserCategoryKey
  )
  if (userCategories?.includes(payload?.userCategoryKey)) {
   res = true
  }
 }
 return res
}

export const formatDate = (dateString: string) => {
 const date = new Date(dateString)

 return date
  .toLocaleDateString('en-GB', {
   day: '2-digit',
   month: 'short',
   year: 'numeric',
  })
  .replace(/ /g, '-')
}

export const formatDatePayload = (date: Date | string) => {
 if (!date) return undefined
 return moment(date).format('DD-MM-YYYY')
}

export const formatDateTime = (dateString: string) => {
 if (!dateString) return '-'

 const date = new Date(dateString)

 const day = date.getDate().toString().padStart(2, '0')

 const month = date.toLocaleString('en-GB', {
  month: 'short',
 })
 const year = date.getFullYear()

 const hours = date.getHours().toString().padStart(2, '0')
 const minutes = date
  .getMinutes()
  .toString()
  .padStart(2, '0')

 return `${day}-${month}-${year}\n${hours}:${minutes} hrs`
}

export const DateTimeFormatter = (dateStr: string) => {
 const date = new Date(dateStr)
 const day = date.getDate().toString().padStart(2, '0')

 const month = date.toLocaleString('en-GB', {
  month: 'short',
 })

 const year = date.getFullYear()

 const time = date.toLocaleString('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
 })

 return `${day}-${month}-${year} | ${time}`
}

export const getTimeAgo = (dateString: string) => {
 const now = new Date().getTime()
 const created = new Date(dateString).getTime()

 const diffInSeconds = Math.floor((now - created) / 1000)

 if (diffInSeconds < 60) {
  return `${diffInSeconds} sec ago`
 }

 const diffInMinutes = Math.floor(diffInSeconds / 60)

 if (diffInMinutes < 60) {
  return `${diffInMinutes} min${
   diffInMinutes > 1 ? 's' : ''
  } ago`
 }

 const diffInHours = Math.floor(diffInMinutes / 60)

 if (diffInHours < 24) {
  return `${diffInHours} hour${
   diffInHours > 1 ? 's' : ''
  } ago`
 }

 const diffInDays = Math.floor(diffInHours / 24)

 if (diffInDays < 30) {
  return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
 }

 const diffInMonths = Math.floor(diffInDays / 30)

 if (diffInMonths < 12) {
  return `${diffInMonths} month${
   diffInMonths > 1 ? 's' : ''
  } ago`
 }

 const diffInYears = Math.floor(diffInMonths / 12)

 return `${diffInYears} year${
  diffInYears > 1 ? 's' : ''
 } ago`
}
